import { Accounting } from '~/types/accounting';
import { ServiceStatusType, ServiceSource, EffectiveDateSource, ServiceChangeItemType } from '../service/model';
import * as serviceBillingModel from '../service-billing/selectors';
import { isOngoing } from '../service-billing/selectors';
import { isRecurring as isBillingRecurring } from '../service-billing/trigger-predicates';
import * as serviceCostModel from '../service-cost';
export const serviceLabelsTextsPath = 'service.model.validation';
export function supportsPriceIncrease(param) {
    let { billing } = param;
    return isBillingRecurring(billing);
}
export function id(service) {
    return service.id;
}
export function cost(service) {
    return service.cost;
}
export function discount(service) {
    return serviceCostModel.discount(service.cost);
}
export function expiredDiscount(service) {
    return serviceCostModel.expiredDiscount(service.cost);
}
export function billingTrigger(service) {
    return service.billing;
}
export function recurrencePeriod(billing) {
    return billing.recurrencePeriod;
}
export function billingDayOfMonth(service) {
    return serviceBillingModel.billingDayOfMonth(service.billing);
}
export function billingDayOfWeek(service) {
    return serviceBillingModel.billingDayOfWeek(service.billing);
}
export function billingDayOfYear(service) {
    return serviceBillingModel.billingDayOfYear(service.billing);
}
export function maxChargesEnabled(service) {
    return serviceBillingModel.maxChargesEnabled(service.billing);
}
export function maxCharges(service) {
    return serviceBillingModel.maxCharges(service.billing);
}
export function maxChargesLimit(service) {
    return serviceBillingModel.maxChargesLimit(service.billing);
}
export function name(service) {
    return service.name;
}
export function price(param) {
    let { cost } = param;
    return serviceCostModel.price(cost);
}
export function minPrice(service) {
    return serviceCostModel.minPrice(service.cost);
}
export function unitName(param) {
    let { cost } = param;
    return serviceCostModel.unitName(cost);
}
export function unitCap(param) {
    let { cost } = param;
    return serviceCostModel.unitCap(cost);
}
export function hasPreapprovedAmount(param) {
    let { cost } = param;
    const unitCap = serviceCostModel.unitCap(cost);
    return unitCap != null && unitCap.gt(0);
}
export function finalPrice(param, quantity) {
    let { cost } = param;
    return serviceCostModel.finalPrice(cost, quantity);
}
export function applyDiscount(param) {
    let { cost } = param;
    return serviceCostModel.applyDiscount(cost);
}
export function isFree(param) {
    let { cost } = param;
    return serviceCostModel.applyDiscount(cost).eq(0);
}
export function minPriceFinal(param) {
    let { cost } = param;
    return serviceCostModel.minPriceFinal(cost);
}
export function changes(service) {
    return matchStatus(service.status, {
        amended: (status)=>status.amended.changes,
        awaitingApproval: ()=>null,
        completed: (status)=>status.completed.changes || null,
        terminated: ()=>null,
        canceled: ()=>null,
        approved: ()=>null
    });
}
export function isBilledUpfront(service) {
    return serviceBillingModel.isBilledUpfront(service.billing);
}
export function repeatableManualUpfrontToPreserve(originalService) {
    return originalService && serviceBillingModel.isRepeatableManual(originalService.billing) ? isBilledUpfront(originalService) : undefined;
}
export function isSkipBillingOnAcceptance(service) {
    return serviceBillingModel.isSkipBillingOnAcceptance(service.billing);
}
/**
 * returns true for cases:
 * - draft -` isBillOnAcceptance`
 * - draft - `isBillOnEffectiveDate`
 *  - edit agreement - `isBillInvoiceImmediately`
 */ export const isBillOnPeriodBeforeNextBilling = (service)=>{
    if (startBillingAt(service)) {
        return false;
    }
    if (!serviceBillingModel.isOngoing(service.billing)) {
        return false;
    }
    return isBilledUpfront(service) && !isSkipBillingOnAcceptance(service);
};
export const isBillOnAcceptance = isBillOnPeriodBeforeNextBilling;
export const isBillOnEffectiveDate = isBillOnPeriodBeforeNextBilling;
export const isBillInvoiceImmediately = isBillOnPeriodBeforeNextBilling;
export const isQuarterly = (service)=>serviceBillingModel.isQuarterly(service.billing);
export const isMonthly = (service)=>serviceBillingModel.isMonthly(service.billing);
export const isWeekly = (service)=>serviceBillingModel.isWeekly(service.billing);
export const isBiWeekly = (service)=>serviceBillingModel.isBiWeekly(service.billing);
export function isAmended(status) {
    return status.type === ServiceStatusType.Amended;
}
export function isApproved(status) {
    return status.type === ServiceStatusType.Approved;
}
export function isAwaitingApproval(status) {
    return status.type === ServiceStatusType.AwaitingApproval;
}
export function requiresClientApproval(service) {
    return matchStatus(service.status, {
        amended: ()=>true,
        awaitingApproval: ()=>true,
        completed: ()=>false,
        terminated: ()=>false,
        canceled: ()=>false,
        approved: ()=>false
    });
}
export function isPaused(service) {
    return serviceBillingModel.isPaused(service.billing);
}
export function isProrated(service) {
    return serviceBillingModel.isProrated(service.billing);
}
export function hasAmendments(service) {
    const serviceChanges = changes(service);
    return !!serviceChanges && Object.keys(serviceChanges).some((k)=>serviceChanges[k] != null);
}
export function note(service) {
    return matchStatus(service.status, {
        amended: (status)=>status.amended.note,
        awaitingApproval: (status)=>status.awaitingApproval.note,
        completed: ()=>null,
        terminated: ()=>null,
        canceled: ()=>null,
        approved: ()=>null
    });
}
export function approvalMethod(service) {
    return matchStatus(service.status, {
        amended: (status)=>status.amended.approvalMethod,
        awaitingApproval: (status)=>status.awaitingApproval.approvalMethod || null,
        completed: ()=>null,
        terminated: ()=>null,
        canceled: ()=>null,
        approved: ()=>null
    });
}
export function hasAutoAmendments(service) {
    const apMethod = approvalMethod(service);
    return !!apMethod && apMethod.isAutoApproval();
}
export function hasApprovalMethod(service) {
    return !!approvalMethod(service);
}
export function autoApprovalDate(service) {
    var _apMethod_autoApprovalDate;
    const apMethod = approvalMethod(service);
    return apMethod === null || apMethod === void 0 ? void 0 : (_apMethod_autoApprovalDate = apMethod.autoApprovalDate()) === null || _apMethod_autoApprovalDate === void 0 ? void 0 : _apMethod_autoApprovalDate.getTime();
}
export function isMandatory(participation) {
    return participation.type === 'mandatory';
}
export function isOptional(participation) {
    return participation.type === 'optional';
}
export function isTerminated(status) {
    return status.type === ServiceStatusType.Terminated;
}
export function isCompleted(status) {
    return status.type === ServiceStatusType.Completed;
}
export function isCanceled(status) {
    return status.type === ServiceStatusType.Canceled;
}
export function isStagedForRemoval(service, originalService) {
    return isCanceled(service.status) && originalService != null && isAwaitingApproval(originalService.status);
}
export function canHoldNote(status) {
    return matchStatus(status, {
        amended: ()=>true,
        awaitingApproval: ()=>true,
        completed: ()=>false,
        terminated: ()=>false,
        canceled: ()=>false,
        approved: ()=>false
    });
}
export function hasEnded(status) {
    return matchStatus(status, {
        amended: ()=>false,
        awaitingApproval: ()=>false,
        completed: ()=>true,
        terminated: ()=>true,
        canceled: ()=>true,
        approved: ()=>false
    });
}
export function becameTerminated(service, original) {
    return isTerminated(service.status) && !isTerminated(original.status);
}
export function becameCanceled(service, original) {
    return isCanceled(service.status) && !isCanceled(original.status);
}
export function effectiveDate(param) {
    let { status } = param;
    return matchStatus(status, {
        amended: (status)=>status.date,
        awaitingApproval: ()=>null,
        completed: (status)=>status.date,
        terminated: (status)=>status.date,
        canceled: ()=>null,
        approved: (status)=>status.date
    });
}
export function startBillingAt(service) {
    return isOngoing(service.billing) ? userSetStartBillingAt(service) : null;
}
export function userSetStartBillingAt(param) {
    let { status } = param;
    return matchStatus(status, {
        amended: (status)=>status.amended.effectiveDateSource === EffectiveDateSource.User ? status.amended.effectiveDate : null,
        approved: (status)=>status.approved.effectiveDateSource === EffectiveDateSource.User ? status.approved.effectiveDate : null,
        awaitingApproval: (status)=>status.awaitingApproval.effectiveDateSource === EffectiveDateSource.User ? status.awaitingApproval.effectiveDate : null,
        canceled: ()=>null,
        completed: (status)=>status.completed.effectiveDateSource === EffectiveDateSource.User ? status.completed.effectiveDate : null,
        terminated: ()=>null
    });
}
export function amendmentDate(param) {
    let { status } = param;
    return matchStatus(status, {
        amended: (status)=>status.amended.amendedOn,
        awaitingApproval: (status)=>status.awaitingApproval.addedOn,
        completed: ()=>null,
        terminated: ()=>null,
        canceled: ()=>null,
        approved: ()=>null
    });
}
export function priceIncrease(param) {
    let { cost } = param;
    return serviceCostModel.priceIncrease(cost);
}
export function nextYearPrice(service) {
    return serviceCostModel.nextYearPrice(service.cost);
}
export function integratedClassId(service) {
    return service.integrations.qboIntegratedClassId();
}
export function isIntegratedClassSet(service) {
    return service.integrations.isQboClassSet();
}
export const hasFinancialCentsIntegration = (service)=>Boolean(service.bundle ? service.bundle.services.find((s)=>s.integrations.financialCentsIntegration()) : service.integrations.financialCentsIntegration());
export const hasKarbonIntegration = (service)=>Boolean(service.bundle ? service.bundle.services.find((s)=>s.integrations.karbonIntegration()) : service.integrations.karbonIntegration());
export function isFromAmendment(service) {
    return service.source === ServiceSource.Amendment;
}
export function isSuitableForTimeActivities(service) {
    return serviceBillingModel.isManual(billingTrigger(service)) && serviceCostModel.isVariablePrice(service.cost) && isApproved(service.status);
}
export function formatRecurrencePeriod(service, texts) {
    return serviceBillingModel.extendedRecurrencePeriodText(serviceBillingModel.extendedRecurrencePeriod(service.billing), false, texts);
}
export function formatBillingTrigger(service, labels, triggerLabels) {
    const manual = ()=>labels.manualLabel();
    const automatic = ()=>labels.automaticLabel();
    return serviceBillingModel.match(service.billing, {
        onApproval: automatic,
        ongoing: automatic,
        repeatableManual: manual,
        oneTimeManual: manual,
        oneTimeInStages: (billing)=>serviceBillingModel.stagesTriggerLabel(billing.oneTimeInStages, triggerLabels),
        onDemand: manual
    });
}
export function stagesTriggerLabel(service, texts) {
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    return oneTimeInStages ? serviceBillingModel.stagesTriggerLabel(oneTimeInStages, texts) : null;
}
export function integratedId(param) {
    let { service, accounting } = param;
    switch(accounting){
        case Accounting.QBO:
            return service.integrations.qboIntegratedId();
        case Accounting.Xero:
            return service.integrations.xeroIntegratedId();
    }
}
export function limitedTimeDiscountOffer(service) {
    return isAwaitingApproval(service.status) ? service.status.awaitingApproval.limitedTimeDiscountOffer : undefined;
}
export function limitedTimeDiscountOfferExpirationType(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.DiscountExpirationType();
}
export function limitedTimeDiscountOfferExpirationDays(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.ExpirationDays();
}
export function limitedTimeDiscountOfferExpirationDate(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.ExpirationDate();
}
export function limitedTimeDiscountOfferExpiration(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.Expiration();
}
export function hasUnbillableStatus(service) {
    return matchStatus(service.status, {
        awaitingApproval: ()=>false,
        amended: ()=>false,
        approved: ()=>false,
        completed: ()=>true,
        terminated: ()=>true,
        canceled: ()=>true
    });
}
export function matchStatus(status, matcher) {
    switch(status.type){
        case ServiceStatusType.Amended:
            return matcher.amended(status);
        case ServiceStatusType.AwaitingApproval:
            return matcher.awaitingApproval(status);
        case ServiceStatusType.Completed:
            return matcher.completed(status);
        case ServiceStatusType.Terminated:
            return matcher.terminated(status);
        case ServiceStatusType.Canceled:
            return matcher.canceled(status);
        case ServiceStatusType.Approved:
            return matcher.approved(status);
    }
}
export function isBundle(service) {
    return !!service.bundle;
}
export function includedServices(service) {
    var _service_bundle;
    return ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) || null;
}
export function matchChangeItem(item, visitor) {
    switch(item.type){
        case ServiceChangeItemType.Pricing:
            return visitor.pricing(item);
        case ServiceChangeItemType.Discount:
            return visitor.discount(item);
        case ServiceChangeItemType.BillingTrigger:
            return visitor.billingTrigger(item);
        case ServiceChangeItemType.Description:
            return visitor.description(item);
        case ServiceChangeItemType.Terms:
            return visitor.terms(item);
        case ServiceChangeItemType.PriceIncrease:
            return visitor.priceIncrease(item);
        case ServiceChangeItemType.IncludedServicesDescription:
            return visitor.includedServicesDescription(item);
    }
}
export const findChangeItem = (changeList, type)=>changeList.find((item)=>item.type === type);
